import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the filter position type
export type FilterPosition = 'top' | 'left';

// Define theme type
export type ThemeMode = 'light' | 'dark';

// Define the context interface
interface AppConfigContextType {
  filterPosition: FilterPosition;
  setFilterPosition: (position: FilterPosition) => void;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  // Add other app-wide configuration settings here as needed
}

// Create the context with default values
const AppConfigContext = createContext<AppConfigContextType>({
  filterPosition: 'top', // Default to top position (current implementation)
  setFilterPosition: () => {}, // Placeholder function
  themeMode: 'dark', // Default to dark mode (current implementation)
  setThemeMode: () => {}, // Placeholder function
  toggleTheme: () => {}, // Placeholder function
});

// Custom hook for using the context
export const useAppConfig = () => useContext(AppConfigContext);

// Provider component
interface AppConfigProviderProps {
  children: ReactNode;
}

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  // Initialize state from localStorage if available
  const [filterPosition, setFilterPosition] = useState<FilterPosition>(() => {
    const savedPosition = localStorage.getItem('filterPosition');
    if (savedPosition) {
      return savedPosition as FilterPosition;
    }
    // Default based on screen size
    return window.innerWidth >= 1024 ? 'left' : 'top';
  });

  // Initialize theme mode from localStorage if available
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const savedTheme = localStorage.getItem('themeMode');
    return (savedTheme as ThemeMode) || 'dark'; // Default to dark mode
  });

  // Toggle theme function
  const toggleTheme = () => {
    setThemeMode(prevMode => prevMode === 'dark' ? 'light' : 'dark');
  };

  // Save to localStorage when preferences change
  useEffect(() => {
    localStorage.setItem('filterPosition', filterPosition);
  }, [filterPosition]);

  // Adjust filter position based on screen size when app loads
  useEffect(() => {
    const checkScreenSize = () => {
      const isLargeScreen = window.innerWidth >= 1024;
      if (isLargeScreen && filterPosition === 'top') {
        setFilterPosition('left');
      } else if (!isLargeScreen && filterPosition === 'left') {
        setFilterPosition('top');
      }
    };
    
    // Run on mount
    checkScreenSize();
    
    // Also set up a resize listener
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [filterPosition, setFilterPosition]);

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  // Context value
  const value = {
    filterPosition,
    setFilterPosition,
    themeMode,
    setThemeMode,
    toggleTheme,
  };

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};
